// Landing page app shell: game directory + unified profile/stats pages, all
// behind the shared hamburger menu. Auth runs in "direct" mode (no backend).

import { useEffect, useState } from "react";
import {
  AppShell,
  AuthCallback,
  AuthProvider,
  ProfilePage,
  StatsPage,
  parseRoute,
  type Route,
} from "@couplesgames/frontend-core";
import { GameDirectory } from "./pages/GameDirectory.js";

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <Router />
    </AuthProvider>
  );
}

function Router(): JSX.Element {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (route.kind === "auth-callback") return <AuthCallback />;

  let content: JSX.Element;
  if (route.kind === "home") {
    content = <GameDirectory />;
  } else if (route.kind === "profile") {
    content = <ProfilePage />;
  } else if (route.kind === "stats") {
    content = <StatsPage />;
  } else if (route.kind === "room") {
    // Rooms only exist on game subdomains; treat as not-found here.
    content = <NotFound path={route.code} />;
  } else {
    content = <NotFound path={route.path} />;
  }

  return <AppShell>{content}</AppShell>;
}

function NotFound({ path }: { path: string }): JSX.Element {
  return (
    <main className="flex min-h-dvh items-center justify-center px-6">
      <div className="max-w-sm w-full text-center space-y-3">
        <h1 className="text-2xl font-semibold">Not found</h1>
        <p className="text-slate-400 text-sm break-all">{path}</p>
        <a href="/" className="text-sky-400 underline text-sm">
          back home
        </a>
      </div>
    </main>
  );
}
